import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { privateAlbumService } from '@/services/privateAlbumService';
import { supabase } from '@/integrations/supabase/client';

interface AlbumLimits {
  maxAlbums: number;
  maxVideosPerAlbum: number;
  currentAlbumCount: number;
  currentVideoCount: number;
  canCreateAlbum: boolean;
  canAddVideo: boolean;
  subscriptionTier: string;
}

export const usePrivateAlbumLimits = () => {
  const { user } = useAuth();
  const [limits, setLimits] = useState<AlbumLimits>({
    maxAlbums: 2,
    maxVideosPerAlbum: 1,
    currentAlbumCount: 0,
    currentVideoCount: 0,
    canCreateAlbum: false,
    canAddVideo: false,
    subscriptionTier: 'basic'
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchLimits();
    }
  }, [user]);

  const fetchLimits = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user subscription tier
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('subscription_tier')
        .eq('user_id', user.id)
        .single();

      const subscriptionTier = profile?.subscription_tier || 'basic';
      
      // Set limits based on subscription tier
      let maxAlbums = 2; // basic/free tier
      let maxVideosPerAlbum = 1;
      
      if (subscriptionTier === 'premium_plus') {
        maxAlbums = 10;
        maxVideosPerAlbum = 1; // Still 1 video per album
      }

      // Get current counts
      const { albumCount, videoCount } = await privateAlbumService.getUserAlbumCounts(user.id);

      setLimits({
        maxAlbums,
        maxVideosPerAlbum,
        currentAlbumCount: albumCount,
        currentVideoCount: videoCount,
        canCreateAlbum: albumCount < maxAlbums,
        canAddVideo: true, // Will be checked per album
        subscriptionTier
      });
    } catch (error) {
      console.error('Error fetching album limits:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanAddVideoToAlbum = async (albumId: string): Promise<boolean> => {
    try {
      const media = await privateAlbumService.getAlbumMedia(albumId);
      const videoCount = media.filter(m => m.media_type === 'video').length;
      return videoCount < limits.maxVideosPerAlbum;
    } catch {
      return false;
    }
  };

  const refreshLimits = () => {
    fetchLimits();
  };

  return {
    limits,
    loading,
    checkCanAddVideoToAlbum,
    refreshLimits
  };
};